import { useCallback, useEffect, useRef, useState } from "react";
import {
  api,
  INVENTORY_LOCATIONS,
  inventoryLocationLabel,
  isInventoryLocation,
  type Driver,
  type InventoryItem,
  type InventoryLocation,
} from "../api";
import { goBack, pushScreen } from "../backstack";
import BackHeader from "../components/BackHeader";
import NetSpeed from "../components/NetSpeed";
import DriverPicker from "../screens/DriverPicker";
import ItemDetail, { itemTitle } from "./ItemDetail";
import ItemForm from "./ItemForm";

type View = { name: "list" } | { name: "new"; files: File[] } | { name: "detail"; id: number };

const SEARCH_DEBOUNCE_MS = 250;
const FILTER_KEY = "inventoryLocationFilter";

// null = every place.
function loadFilter(): InventoryLocation | null {
  const saved = localStorage.getItem(FILTER_KEY);
  return isInventoryLocation(saved) ? saved : null;
}

// Same remembered user as the box tracker, so picking once covers both tools.
function loadDriver(): Driver | null {
  try {
    return JSON.parse(localStorage.getItem("driver") ?? "null");
  } catch {
    return null;
  }
}

function Tile({ item, showPlace, onOpen }: { item: InventoryItem; showPlace: boolean; onOpen: () => void }) {
  const cover = item.photoIds[0];
  return (
    <button
      onClick={onOpen}
      className="relative aspect-square overflow-hidden rounded-[18px] bg-white transition-transform active:scale-[0.97] border border-line-soft shadow-card"
    >
      {cover ? (
        <img src={api.inventoryPhotoUrl(cover, true)} alt="" loading="lazy" className="size-full object-cover" />
      ) : (
        <div className="flex size-full items-center justify-center text-3xl text-ink/20">📦</div>
      )}
      {showPlace && (
        <span
          className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-xs font-bold text-ink ${
            item.location === "kitchen" ? "bg-lemon" : item.location === "warehouse" ? "bg-white/90" : "bg-lavender"
          }`}
        >
          {inventoryLocationLabel(item.location)}
        </span>
      )}
      {item.photoIds.length > 1 && (
        <span className="absolute right-2 top-2 rounded-full bg-ink/70 px-2 py-0.5 text-xs font-bold text-white">
          {item.photoIds.length}
        </span>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/85 via-ink/55 to-transparent px-3 pb-2.5 pt-8 text-left">
        <p className={`line-clamp-2 text-sm font-semibold leading-tight text-white ${item.description ? "" : "opacity-70"}`}>
          {itemTitle(item)}
        </p>
      </div>
    </button>
  );
}

// `startNew` opens on the form for a new item (the order screen's Create menu); a browser will not open the camera
// by itself, so the photo is taken from the form.
export default function InventoryApp({ onHome, startNew = false }: { onHome: () => void; startNew?: boolean }) {
  const [driver, setDriver] = useState<Driver | null>(loadDriver);
  const [view, setView] = useState<View>(startNew ? { name: "new", files: [] } : { name: "list" });
  const [query, setQuery] = useState("");
  const [place, setPlace] = useState<InventoryLocation | null>(loadFilter);
  const [items, setItems] = useState<InventoryItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const requestId = useRef(0);

  const load = useCallback((q: string, location: InventoryLocation | null) => {
    const mine = ++requestId.current;
    api
      .inventory({ q: q.trim() || undefined, location: location ?? undefined })
      .then((page) => {
        if (mine !== requestId.current) return; // a newer search already answered
        setItems(page.items);
        setTotal(page.total);
        setHasMore(page.items.length === page.pageSize);
        setError(null);
      })
      .catch(() => mine === requestId.current && setError("Couldn't load the items. Check the connection."));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(query, place), query ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [query, place, load]);

  // Tap a place to show only that one; tap it again for everything.
  const togglePlace = (value: InventoryLocation) => {
    const next = place === value ? null : value;
    setPlace(next);
    if (next) localStorage.setItem(FILTER_KEY, next);
    else localStorage.removeItem(FILTER_KEY);
  };

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const openView = (next: View) => {
    setView(next);
    pushScreen(() => setView({ name: "list" }));
  };
  // Opened on a new item: back goes to the list, as after a tap.
  useEffect(() => {
    if (startNew) pushScreen(() => setView({ name: "list" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = async () => {
    if (!items?.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await api.inventory({
        q: query.trim() || undefined,
        location: place ?? undefined,
        beforeId: items[items.length - 1]!.id,
      });
      setItems([...items, ...page.items]);
      setHasMore(page.items.length === page.pageSize);
    } catch {
      setError("Couldn't load more items.");
    } finally {
      setLoadingMore(false);
    }
  };

  const replaceItem = (updated: InventoryItem) =>
    setItems((list) => list?.map((i) => (i.id === updated.id ? updated : i)) ?? [updated]);

  if (!driver) {
    return (
      <DriverPicker
        onPick={(picked) => {
          localStorage.setItem("driver", JSON.stringify(picked));
          setDriver(picked);
        }}
        onBack={onHome}
        title="Who are you?"
        matsentralen={false}
      />
    );
  }

  if (view.name === "new") {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 pb-6 pt-4">
        <ItemForm
          driver={driver}
          defaultLocation={place ?? undefined}
          firstPhotos={view.files}
          onSaved={(item, note) => {
            // Show the new item straight away, whatever the current search is.
            setItems((list) => [item, ...(list ?? []).filter((i) => i.id !== item.id)]);
            setTotal((n) => n + 1);
            // Saved into the other place than the one being shown: switch so it isn't hidden.
            if (place && item.location !== place) togglePlace(item.location);
            setToast(note ?? "Item saved");
            goBack();
          }}
          onCancel={goBack}
        />
      </div>
    );
  }

  if (view.name === "detail") {
    const item = items?.find((i) => i.id === view.id);
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 pb-6 pt-4">
        {item ? (
          <ItemDetail
            item={item}
            driver={driver}
            onChange={replaceItem}
            onDeleted={() => {
              setItems((list) => list?.filter((i) => i.id !== item.id) ?? null);
              setTotal((n) => Math.max(0, n - 1));
              setToast("Item deleted");
              goBack();
            }}
            onSearchTag={(tag) => {
              setQuery(tag);
              goBack();
            }}
            onBack={goBack}
          />
        ) : (
          <BackHeader title="Item" onBack={goBack} />
        )}
      </div>
    );
  }

  const searching = query.trim().length > 0;
  const narrowed = searching || place !== null;

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col gap-4 px-4 pb-28 pt-4">
      <BackHeader
        title="Items"
        logo
        onBack={onHome}
        action={
          <button
            onClick={() => setDriver(null)}
            className="flex min-h-11 shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold border border-line"
            title="Switch user"
          >
            <span className="inline-block size-2 rounded-full bg-rose" />
            {driver.name}
          </button>
        }
      />

      <div className="sticky top-2 z-10">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search description, brand, tags…"
          className="h-14 w-full rounded-[14px] bg-white px-5 font-semibold outline-none placeholder:text-ink/40 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20"
        />
        {searching && (
          <button
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="absolute right-2 top-2 flex size-10 items-center justify-center rounded-full bg-blush font-bold text-rose-ink"
          >
            ✕
          </button>
        )}
      </div>

      {/* One row, never scrolls: Warehouse and Kitchen keep their width, the stores share the rest. */}
      <div className="flex gap-1">
        {INVENTORY_LOCATIONS.map((loc) => {
          const on = place === loc.value;
          return (
            <button
              key={loc.value}
              onClick={() => togglePlace(loc.value)}
              aria-pressed={on}
              aria-label={loc.store ? `Store ${loc.label}` : loc.label}
              className={`h-9 rounded-full text-xs font-bold shadow-sm transition-colors ${
                loc.store ? "min-w-0 flex-1 px-0" : "shrink-0 px-2.5"
              } ${on ? "bg-ink text-white" : "bg-white text-ink/50 border border-line"}`}
            >
              {loc.label}
            </button>
          );
        })}
      </div>

      {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}
      {items === null && !error && <p className="py-8 text-center font-semibold text-ink/50">Loading…</p>}

      {/* One thin line: item count left, connection quality right. */}
      {items && (
        <div className="-mt-1 flex items-center justify-between px-1 text-[11px] font-semibold leading-none text-ink/50">
          <span className="min-w-0 truncate">
            {narrowed
              ? `${items.length}${hasMore ? "+" : ""} ${searching ? (items.length === 1 ? "match" : "matches") : `in ${inventoryLocationLabel(place ?? "")}`} of ${total}`
              : `${total} ${total === 1 ? "item" : "items"}`}
          </span>
          <NetSpeed />
        </div>
      )}
      {items && items.length === 0 && (
        <p className="py-8 text-center font-semibold text-ink/50">
          {searching
            ? "Nothing matches that search."
            : place
              ? `Nothing in ${inventoryLocationLabel(place)} yet. Tap Camera and take a photo.`
              : "Nothing here yet. Tap Camera and take a photo of the first thing."}
        </p>
      )}

      {!!items?.length && (
        <div className="grid grid-cols-2 gap-3">
          {items.map((item) => (
            <Tile
              key={item.id}
              item={item}
              showPlace={place === null}
              onOpen={() => openView({ name: "detail", id: item.id })}
            />
          ))}
        </div>
      )}

      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loadingMore}
          className="mx-auto rounded-full bg-white px-6 py-3 font-bold disabled:opacity-60 border border-line"
        >
          {loadingMore ? "Loading…" : "Show more"}
        </button>
      )}

      {/* One tap from the list straight into the camera — the fast path. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = "";
          if (files.length) openView({ name: "new", files });
        }}
      />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 mx-auto flex max-w-md justify-center px-4 pb-5">
        <div className="pointer-events-auto flex shrink-0 items-center gap-2">
          <button
            onClick={() => cameraRef.current?.click()}
            className="flex h-16 items-center gap-2 rounded-[14px] bg-rose-grad px-8 text-lg font-bold text-white shadow-pop transition-transform active:scale-95 shadow-button"
          >
            <span aria-hidden>📷</span> Camera
          </button>
          {/* Same form without going through the camera (photos from the library). */}
          <button
            onClick={() => openView({ name: "new", files: [] })}
            aria-label="Add item from the photo library"
            title="Add from library"
            className="flex size-16 items-center justify-center rounded-full bg-white text-2xl shadow-pop transition-transform active:scale-95"
          >
            <span aria-hidden>🖼️</span>
          </button>
        </div>
      </div>

      {toast && (
        <div className="pop-in fixed inset-x-4 top-4 z-50 mx-auto max-w-md rounded-[14px] bg-ink px-4 py-3 text-center text-sm font-semibold text-white shadow-pop">
          {toast}
        </div>
      )}
    </div>
  );
}
